import React, {MouseEvent, useCallback} from 'react'
import {MemoOptionIconButton} from 'module/Memo/component/MemoOptionIconButton'
import {HighlightedTextComponent} from 'component/HighlightedTextComponent'
import {SlctOptionProps} from 'module/Slct/type/SlctType'
import {getOptionLabel} from 'module/Slct/util/getOptionLabel'
import {css} from '@emotion/react'
import {Tooltip} from '@mui/material'
import {zIndexDropDown} from 'module/Theme/BaseMuiTheme'
import {cssSelectable, cssSlctEllipsis, cssSlctOption, cssSlctOptionActive, cssSlctOptionDisabled, cssSlctOptionMemo, cssSlctOptionRoot} from 'module/Slct/style/SlctStyle'

const cssBull = css`
	font-size: 36px;
	line-height: 17px;
	user-select: none;
	pointer-events: none;
`

export function SlctOptionComponent<ModelType extends object | void, ValueType extends string | number>({
	...props
}: SlctOptionProps<ModelType, ValueType>) {
	const {index, options, memo, inputValue, cursor, setCursor, displayFormat, onClickOption} = props
	const option = options[index]

	const {memoKey, memoSize} = memo || {}
	const selected = cursor === index
	const displayValue = getOptionLabel(displayFormat, option)
	const cssStateColorList = []

	// disabled status overrides others!
	if (option.disabled) {
		cssStateColorList.push(cssSlctOptionDisabled)
	} else if (selected) {
		cssStateColorList.push(cssSelectable)
	} else if (option.active) {
		cssStateColorList.push(cssSlctOptionActive)
	}
	const dot = option.dotcolor?.trim() ? <div css={cssBull} style={{color: option.dotcolor}}>&bull;</div> : null
	if (option.textcss?.trim()) cssStateColorList.push(css(option.textcss))

	const onClick = useCallback((evt: MouseEvent<HTMLDivElement>) => {
		if (evt.button === 0) {
			onClickOption(option, evt)
		}
	}, [option, onClickOption])

	const onMouseMove = useCallback(() => {
		setCursor(index)
	}, [index, setCursor])

	let optionWrapper = <div
		onClick={onClick}
		css={[cssSlctOption, cssSlctEllipsis]}
	>
		{typeof displayValue === 'string' &&
			<HighlightedTextComponent text={displayValue} searchTerm={inputValue}/>
			||
			displayValue
		}
	</div>
	if (option.tooltip?.trim()) {
		// zIndex of tooltip is normally under SlctMenu -> fix it by moving it above SlctMenu
		optionWrapper = <Tooltip componentsProps={{popper: {style: {zIndex: zIndexDropDown + 1}}}} disableInteractive title={<div dangerouslySetInnerHTML={{__html: option.tooltip}}/>}>
			{optionWrapper}
		</Tooltip>
	}

	return <div
		className={['SlctOptionComponent', selected && 'SlctCursor', option.active && 'SlctOptionActive'].filter(x => x).join(' ')}
		data-value={JSON.stringify(option.value)}
		css={[cssSlctOptionRoot, ...cssStateColorList]}
		style={selected || option.active ? undefined : {color: option.color, background: option.background}}
		onMouseMove={onMouseMove}
	>
		{dot}
		{optionWrapper}
		<MemoOptionIconButton
			css={cssSlctOptionMemo}
			memoKey={memoKey}
			memoSize={memoSize}
			element={option}
		/>
	</div>
}